/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';

export type NavigationLink = { label: string; name: string };

type Props = {
  navigationLinks?: Array<NavigationLink>;
};

const styles = {
  navItems: css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 32px;
    padding-top: var(--cds-spacing-150);
    padding-bottom: var(--cds-spacing-150);
    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-100);
    }
  `,
  navLink: (theme: Theme) => css`
    display: block;
    border-radius: 4px;
    color: var(--cds-color-neutral-primary);
    font-family: ${theme.typography.fontFamily};
    padding: 10px 12px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;

    :hover,
    :focus {
      text-decoration: none;
      color: var(--cds-color-blue-800);
      background: var(--cds-color-blue-25);
    }

    :focus {
      outline: 1px solid var(--cds-color-blue-800);
      outline-offset: -1px;
    }
  `,
  activeNavLink: css`
    color: var(--cds-color-blue-800);
    background: var(--cds-color-grey-25);
    text-decoration: underline;
  `,
};

const PageNavigation = ({ navigationLinks }: Props) => {
  const { sectionInView } = usePageNavigation();

  if (!navigationLinks || navigationLinks.length === 0) {
    return null;
  }

  return (
    <nav>
      <OverflowCarousel css={styles.navItems}>
        {navigationLinks.map(({ name, label }) => {
          const isActiveItem = sectionInView === name;
          return (
            <TrackedA
              key={name}
              href={`#${name}`}
              trackingName={`nav_item_${name}`}
              css={[styles.navLink, isActiveItem && styles.activeNavLink]}
              {...(isActiveItem && { 'aria-current': true })}
            >
              {label}
            </TrackedA>
          );
        })}
      </OverflowCarousel>
    </nav>
  );
};

export default PageNavigation;
